import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  validateJoinCodeEligibility,
  verifyEmailAllowedForTeamJoin,
  completeTeamJoinForAuthenticatedUser,
} from "../../services/queries/joinCodeQueries";
import { sendMagicLinkEmail } from "../../utils/auth/sendMagicLinkEmail";
import { auth } from "../../config/firebase";
import { useAppState } from "../../contexts/UserContext";
import { useAuth } from "../../contexts/AuthContext";
import { useCause } from "../../contexts/CauseContext";
import { useTeam } from "../../contexts/TeamContext";
import { useUserLogin } from "../authentication/hooks/useUserAuth";
import { useNetworkStatus } from "../../hooks/useNetworkStatus";

export function normalizeJoinEmail(value: string) {
  return value.trim().toLowerCase();
}

export function isValidJoinEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export type JoinWithCodeStep = "form" | "code";

export interface UseJoinWithCodeFlowOptions {
  /** Where to navigate after successful join (default `/app/home`) */
  navigateAfterJoin?: string;
}

export function useJoinWithCodeFlow(options?: UseJoinWithCodeFlowOptions) {
  const navigateAfterJoin = options?.navigateAfterJoin ?? "/app/home";
  const navigate = useNavigate();
  const [joinCode, setJoinCode] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [error, setError] = useState("");
  const [step, setStep] = useState<JoinWithCodeStep>("form");
  const [teamIdPending, setTeamIdPending] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const network = useNetworkStatus() as { isOnline: boolean };
  const isOnline = network.isOnline;

  const { setMoods, refreshData } = useAppState();
  const { setUserId, setEmail } = useAuth();
  const { setCauses } = useCause();
  const { setTeamId } = useTeam();

  const loginUser = useUserLogin({
    setUserId,
    setEmail,
    setMoods,
    setCauses,
    refreshData,
  });

  const handleJoin = useCallback(async () => {


    console.log("handle join")
    setError("");

    if (!joinCode.trim()) {
      setError("Please enter a join-team code");
      return;
    }

    setIsValidating(true);

    let resolvedTeamId: string | null = null;
    let resolvedOrganizationId: string | null = null;

    try {

      console.log("in progress")
      const eligibility = await validateJoinCodeEligibility(joinCode.trim());
      if (!eligibility.ok) {
        setError(eligibility.error);
        return;
      }

      console.log("eligible")
      resolvedTeamId = eligibility.teamId;
      resolvedOrganizationId = eligibility.organizationId;
      setTeamIdPending(eligibility.teamId);
      setOrganizationId(eligibility.organizationId);
    } catch (e: unknown) {
      const msg =
        e instanceof Error ? e.message : "Could not send the login code.";
      setError(msg);
      return;
    } finally {
      setIsValidating(false);
    }

    if (!resolvedTeamId || !resolvedOrganizationId) {
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      const user = auth.currentUser;
      if (!user?.email) {
        throw new Error("Not signed in. Please verify the code again.");
      }

      const emailCheck = await verifyEmailAllowedForTeamJoin(
        resolvedTeamId,
        user.email
      );
      if (!emailCheck.ok) {
        throw new Error(emailCheck.error);
      }

      const { getAccount } = await import("../../api/accountApi");
      const existingAccount = await getAccount().catch(() => null);
      const isExistingUser = !!existingAccount;

      await loginUser(user.email, user.uid, isExistingUser);

      const joinResult = await completeTeamJoinForAuthenticatedUser({
        organizationId: resolvedOrganizationId,
        teamId: resolvedTeamId,
      });

      if (!joinResult.ok) {
        throw new Error(joinResult.error);
      }

      setTeamId(resolvedTeamId);
      setTimeout(() => {
        navigate(navigateAfterJoin, { replace: true });
      }, 1100);
    } catch (e: unknown) {
      const msg =
        e instanceof Error ? e.message : "Could not finish joining the team.";
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [joinCode, loginUser, navigate, navigateAfterJoin, setTeamId]);

  const handleContinue = useCallback(async () => {
    setError("");

    if (!joinCode.trim()) {
      setError("Please enter a join-team code");
      return;
    }

    if (!isValidJoinEmail(emailInput)) {
      setError("Enter a valid email address to receive a login code.");
      return;
    }

    if (!isOnline) {
      setError("You are offline. Please check your connection.");
      return;
    }

    setIsValidating(true);
    try {
      if (!auth.currentUser) {
        const { signInAnonymously } = await import("firebase/auth");
        await signInAnonymously(auth);
      }

      const eligibility = await validateJoinCodeEligibility(joinCode.trim());
      if (!eligibility.ok) {
        setError(eligibility.error);
        return;
      }

      const emailNorm = normalizeJoinEmail(emailInput);
      await sendMagicLinkEmail(emailNorm);

      setTeamIdPending(eligibility.teamId);
      setOrganizationId(eligibility.organizationId);
      setStep("code");
    } catch (e: unknown) {
      const msg =
        e instanceof Error ? e.message : "Could not send the login code.";
      setError(msg);
    } finally {
      setIsValidating(false);
    }
  }, [joinCode, emailInput, isOnline]);

  const handleCodeVerified = useCallback(async () => {
    setError("");
    setIsLoading(true);
    try {
      const user = auth.currentUser;
      if (!user?.email) {
        throw new Error("Not signed in. Please verify the code again.");
      }

      const emailNorm = normalizeJoinEmail(emailInput);
      if (user.email.toLowerCase() !== emailNorm) {
        throw new Error(
          "Signed-in email does not match. Use the same email you entered."
        );
      }

      if (!teamIdPending) {
        throw new Error("Missing team context. Start over from the join code.");
      }

      if (!organizationId) {
        throw new Error(
          "Missing organization context. Start over from the join code."
        );
      }

      const emailCheck = await verifyEmailAllowedForTeamJoin(
        teamIdPending,
        emailNorm
      );
      if (!emailCheck.ok) {
        throw new Error(emailCheck.error);
      }

      const { getAccount } = await import("../../api/accountApi");
      const existingAccount = await getAccount().catch(() => null);
      const isExistingUser = !!existingAccount;

      console.log("completeTeamJoinForAuthenticatedUser");

      await loginUser(emailNorm, user.uid, isExistingUser);

      const joinResult = await completeTeamJoinForAuthenticatedUser({
        organizationId: organizationId,
        teamId: teamIdPending,
      });

      console.log("completeTeamJoinForAuthenticatedUser 1");

      if (!joinResult.ok) {
        throw new Error(joinResult.error);
      }

      setTeamId(teamIdPending);
      setTimeout(() => {
        navigate(navigateAfterJoin, { replace: true });
      }, 1100);
    } catch (e: unknown) {
      const msg =
        e instanceof Error ? e.message : "Could not finish joining the team.";
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [
    emailInput,
    teamIdPending,
    organizationId,
    teamIdPending,
    loginUser,
    setTeamId,
    navigate,
    navigateAfterJoin,
  ]);

  const handleBackToForm = useCallback(() => {
    setStep("form");
    setError("");
    setTeamIdPending(null);
    setOrganizationId(null);
  }, []);

  return {
    joinCode,
    setJoinCode,
    emailInput,
    setEmailInput,
    error,
    setError,
    step,
    isValidating,
    isLoading,
    isOnline,
    handleContinue,
    handleCodeVerified,
    handleJoin,
    handleBackToForm,
  };
}
